<template>
  <fragment>
    <div class="row">
      <div class="col-12 col-lg-6">
        <div class="form-group">
          <label><strong>Name:</strong></label>
          <input v-model="filter" type="text"
                 class="form-control"
                 placeholder="Filter names...">
        </div>
      </div>
      <div class="col-12 col-lg-6 text-right">
        <div class="form-group">
          <label>&nbsp;</label><br>
          <button class="btn btn-ss-standout btn-icon-sm" @click="() => initQuickCreateModal(podcast)">
            <i class="la la-plus" /> {{ getItemLabel(true) }}
          </button>
        </div>
      </div>
    </div>

    <b-table id="episodes_table"
             :items="filtered_episodes"
             :fields="table_columns"
             :sort-by.sync="sortBy"
             :sort-desc.sync="sortDesc"
             :busy="loading_episodes"
             :show-empty="!filtered_episodes.length"
             :current-page="pagination.current_page"
             :per-page="pagination.per_page"
             responsive="sm">
      <template #table-busy>
        <loader loading-text="Loading Episodes..." />
      </template>
      <template #empty>
        <div v-if="!loading_episodes" class="w-100 empty-content-margin text-center">
          <h5 v-if="!filter">
            Your podcast doesn't have any episodes yet. Use the '+ Episode' button above to add your first Episode.
          </h5>
          <h5 v-else>
            Your podcast doesn't have any episode matching the filter '{{ filter }}'
          </h5>
        </div>
      </template>
      <template v-if="Object.keys(clashing_episodes).length > 0" #cell(clashes)="row">
        <template v-if="clashesOf(row.item)">
          <i :id="`clash-tooltip-${row.item.uuid}`" class="la la-exclamation-triangle font-weight-bold text-ss-danger" style="font-size: 1.5em" />
          <b-tooltip :target="`clash-tooltip-${row.item.uuid}`">
            <p>Season and Episode number combination is duplicated in the following Episodes:</p>
            <ul class="clean-list">
              <li v-for="uuid of clashesOf(row.item)" :key="uuid">
                <a :href="`/episode/${row.item.uuid}/edit?feed_uuid=${podcast.uuid}`" target="_blank">
                  {{ episodes.find(episode => episode.uuid === uuid)?.name }}
                </a>
              </li>
            </ul>
            <p class="font-weight-bold">
              <span v-if="row.item.type !== 'full'">
                ({{ capitalise(row.item.type) }})
              </span>
              Season {{ row.item.season_number }}, Episode {{ row.item.episode_number }}
            </p>
          </b-tooltip>
        </template>
      </template>
      <template #cell(artwork_thumbnail)="data">
        <img :src="data.value" style="max-width:50px;">
      </template>
      <template #cell(published)="data">
        {{ data.value ? 'Yes' : 'No' }}
      </template>
      <template #cell(show_details)="row">
        <div class="btn-group" role="group">
          <share-btn :episode="row.item" :part-of-button-group="true" />

          <button type="button" class="btn btn-ss" @click="row.toggleDetails">
            <span class="la la-play" />
          </button>

          <a :href="`/episode/${row.item.uuid}/edit?feed_uuid=${podcast.uuid}`" class="btn btn-ss">
            <span class="la la-edit" />
          </a>

          <button type="button" class="btn btn-ss-danger" @click="confirmModalThen([row.item], deleteEpisode, 'delete-confirm');">
            <span class="la la-trash" />
          </button>
        </div>
      </template>
      <template #row-details="row">
        <b-card>
          <b-row>
            <b-col class="mb-4">
              <player-iframe-preview :item="row.item"
                                     layout-override="legacy"
                                     :show-embed-code="false"
                                     :show-tools="false"
                                     :show-listen-link="false" />
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="6" sm="4" class="mb-2">
              <p><strong>Publish Start : </strong>{{ row.item.publish_start ?? 'Not Set' }}</p>
            </b-col>
            <b-col cols="6" sm="4" class="mb-2">
              <p><strong>Publish Stop : </strong>{{ row.item.publish_stop ?? 'Not Set' }}</p>
            </b-col>
            <b-col cols="6" sm="4" class="mb-2">
              <p><strong>Created : </strong>{{ row.item.created_at }}</p>
            </b-col>
            <b-col cols="6" sm="4" class="mb-2">
              <p><strong>Duration : </strong> {{ row.item.duration }}</p>
            </b-col>
            <b-col v-if="row.item.tags && row.item.tags.length"
                   cols="12"
                   sm="6"
                   class="mb-2">
              <p>
                <strong>Tags : </strong>
                <active-passive :data-val="row.item.tags"
                                bordered
                                :show-icon="false"
                                hover-text="Show More"
                                toggle-event="click">
                  <template #ap-passive>
                    <multi-select-tag-bag :tags-list="row.item.tags" :limit="15" :show-controls="false" />
                  </template>
                  <template #ap-active>
                    <multi-select-tag-bag :tags-list="row.item.tags" :limit="150" :show-controls="false" />
                  </template>
                </active-passive>
              </p>
            </b-col>
          </b-row>
        </b-card>
      </template>
    </b-table>

    <b-pagination
      id="episodes_table_pagination"
      v-model="pagination.current_page"
      :total-rows="filtered_episodes.length"
      :per-page="pagination.per_page"
      first-class="next-prev-buttons"
      last-class="next-prev-buttons"
      next-class="next-prev-buttons"
      prev-class="next-prev-buttons"
      aria-controls="episodes_table"
      align="right"
      pills />

    <preview-modal name="preview-episode" :is-feed="false" :entity="preview_episode" />
    <delete-confirm-modal :entity-type="getItemLabel()">
      <div class="d-flex flex-column justify-center">
        <div class="text-center">
          You are about to delete episode "<span class="font-weight-bold">{{ staged_models?.at(0)?.name }}"</span>!
        </div>

        <div class="mt-4 pl-2">
          <ss-checkbox id="preserve-storage" v-model="preserve_asset_on_episode_delete">
            Preserve Storage Assets?
          </ss-checkbox>
        </div>
      </div>
    </delete-confirm-modal>
  </fragment>
</template>

<script>
import { mapWritableState, mapActions } from 'pinia';
import { usePodcastManageStore } from "VueStore/app/podcastManage";
import ItemQuickCreateMixin from 'VueMixins/ItemQuickCreateMixin';
import {
  BCard,
  BTable,
  BRow,
  BCol,
} from "bootstrap-vue";
import PlayerIframePreview from "VueComponents/PlayerIframePreview";
import Loader from "VuePartials/Loader";
import DeleteConfirmModal from "VueModals/DeleteConfirmModal";
import PreviewModal from "VueModals/PreviewModal";
import ShareBtn from "VuePartials/Items/EpisodeSettings/ShareBtn";
import {
  formatDate,
  convertToHhmmss,
  formatDateTime,
} from "Helpers/formatters";
import ActivePassive from 'VueComponents/ActivePassive';
import MultiSelectTagBag from "VueComponents/MultiSelectTagBag";
import { capitalise } from "Lib/string-util";
import SsCheckbox from 'VueComponents/SsCheckbox.vue';

export default {
  components: {
    ShareBtn,
    DeleteConfirmModal,
    PreviewModal,
    Loader,
    PlayerIframePreview,
    BCard,
    BTable,
    BRow,
    BCol,
    ActivePassive,
    MultiSelectTagBag,
    SsCheckbox,
  },
  mixins: [ItemQuickCreateMixin],
  data() {
    return {
      pagination: {
        current_page: 1,
        per_page: 30,
      },
      sortBy: 'created_at',
      sortDesc: true,
      serial_table_columns: [
        {
          key: 'artwork_thumbnail',
          label: '',
          class: 'align-middle',
        },
        {
          key: 'name',
          sortable: true,
          class: 'align-middle',
        },
        {
          key: 'type',
          sortable: true,
          class: 'align-middle',
          formatter: value => value?.charAt(0).toUpperCase() + value?.slice(1), // uc first letter
        },
        {
          key: 'season_number',
          label: 'Season',
          sortable: true,
          class: 'align-middle',
        },
        {
          key: 'episode_number',
          label: 'Episode',
          sortable: true,
          class: 'align-middle',
        },
        {
          key: 'published',
          sortable: true,
          class: 'align-middle',
        },
        {
          key: 'show_details',
          label: '',
          class: 'align-middle text-right',
        },
      ],
      episodic_table_columns: [
        {
          key: 'artwork_thumbnail',
          label: '',
          class: 'align-middle',
        },
        {
          key: 'name',
          sortable: true,
          class: 'align-middle',
        },
        {
          key: 'type',
          sortable: true,
          class: 'align-middle',
          formatter: value => value?.charAt(0).toUpperCase() + value?.slice(1), // uc first letter
        },
        {
          key: 'created_at',
          label: 'Created',
          sortable: true,
          class: 'align-middle',
        },
        {
          key: 'publish_start',
          label: 'Publish Date',
          sortable: true,
          class: 'align-middle',
        },
        {
          key: 'published',
          sortable: true,
          class: 'align-middle',
        },
        {
          key: 'show_details',
          label: '',
          class: 'align-middle text-right',
        },
      ],
      filter: null,
      preview_episode: {},
    };
  },
  computed: {
    ...mapWritableState(usePodcastManageStore, ['podcast']),
    ...mapWritableState(usePodcastManageStore, ['episodes']),
    ...mapWritableState(usePodcastManageStore, [
      'preserve_asset_on_episode_delete',
    ]),
    ...mapWritableState(usePodcastManageStore, ['loading_episodes']),
    filtered_episodes() {
      if (this.filter) {
        return _.filter(this.formatted_episodes, (episode) => {
          const lowercase_name = episode.name.toLowerCase();
          const lowercase_filter = this.filter.toLowerCase();
          return lowercase_name.includes(lowercase_filter);
        });
      }

      return this.formatted_episodes;
    },
    formatted_episodes() {
      if (!this.episodes) {
        return [];
      }
      // Filter out all of the garbage.
      return _.map(this.episodes, item => ({
        uuid: item.uuid,
        artwork_thumbnail: item.artwork?.thumbnail_path,
        name: item.name,
        description: item.description,
        published: item.published,
        season_number: item.episode_detail?.season_number ?? 0,
        episode_number: item.episode_detail?.episode_number ?? 0,
        created_at: this.formatDateTime(item.created_at),
        publish_start: item.publish_start ? this.formatDate(item.publish_start) : 'Not Set',
        publish_stop: item.publish_stop ? this.formatDate(item.publish_stop) : item.publish_start ? 'Never' : 'Not Set',
        player_data: item.player_data,
        duration: item.episode_detail?.duration ? this.convertToHhmmss(item.episode_detail.duration) : 'Unknown',
        tags: item.tags,
        type: item.episode_detail?.type ?? null,
        _showDetails: false,
      }));
    },
    table_columns() {
      if (this.podcast.itunes_detail && this.podcast.itunes_detail.type === 'serial') {
        return this.serial_table_columns;
      }

      // if there are clashing episodes, we need a new column for the indicators
      if (Object.keys(this.clashing_episodes).length > 0) {
        return [
          {
            key: 'clashes',
            label: '',
            class: 'align-middle',
          },
          ...this.episodic_table_columns,
        ];
      }

      return this.episodic_table_columns;
    },
    /**
     * An object who's key-vals are in the form `'T,S,E': [ ... ]`, where `T` is the type, and `S` and `E` are the season and episode number.
     *
     * Any arrays in this object contain episode uuids for episodes with clashing type/season/episode numbers.
     *
     * @return { { [string]: string[] } }
     */
    clashing_episodes() {
      const groupings = {};

      // first, make an assoc array like { 'full,1,2': [ ...<episode uuids> ], 'trailer,1,3': [ ... ], ... }
      // this gathers episodes by unique [type, season no., episode no.] groups
      for (const episode of this.episodes) {
        const { uuid, episode_detail } = episode;

        if (episode_detail) {
          const { episode_number, season_number, type } = episode_detail;
          const tuple_str = `${type},${season_number},${episode_number}`;

          groupings[tuple_str] = groupings[tuple_str] ?? [];
          groupings[tuple_str].push(uuid);
        }
      }

      // now filter any groups with just one episode (which is the correct state for an episode for a feed)
      for (const [i, group] of Object.entries(groupings)) {
        if (group.length <= 1) {
          delete groupings[i];
        }
      }

      // anything left is { 'T,S,E': [ ... ] } groups which have more than one episode
      return groupings;
    },
  },
  mounted() {
    EventBus.$on([`episode_preview-closed`], () => {
      $(`#episode_preview`).modal(`hide`);
      this.preview_episode = null;
    });
    EventBus.$on('episode_added', () => {
      this.getEpisodes();
    });
    this.getEpisodes();
  },
  methods: {
    capitalise,
    formatDate,
    formatDateTime,
    convertToHhmmss,
    ...mapActions(usePodcastManageStore, ['getEpisodes', 'deleteEpisode']),
    initEpisodeCreateModal() {
      this.fireModal(`q-create-modal`);
    },
    previewEpisode(episode) {
      // Have to use uuid otherwise we get trimmed data from row.
      this.preview_episode = episode;
      this.fireModal(`preview-episode`);
    },
    /**
     * Returns the clashing uuid group (if there is one) of the given T/S/E tuple, sans the given uuid of the caller.
     *
     * @param { Object } detail
     * @param { string } detail.uuid
     * @param { string } detail.type
     * @param { number } detail.season_number
     * @param { number } detail.episode_number
     * @return { string[] }
     */
    clashesOf({ uuid, type, season_number, episode_number }) {
      const index = `${type},${season_number},${episode_number}`;

      return this.clashing_episodes[index] ?
        this.clashing_episodes[index].filter(other_uuid => other_uuid !== uuid) :
        undefined;
    },
  },
};
</script>

<style lang="scss" scoped>
@use "sass:map";

@use "AssetsSass/_variables" as *;

#episodes_table_pagination {
  :deep(.next-prev-buttons) {
    .page-link {
      background-color: #F7F8FA;
      // background-color: #F0F3FF;
    }
  }

  :deep(.page-item) {
    &.disabled .page-link {
      opacity: 0.5;
    }

    &.active .page-link {
      color: #FFF;
      background-color: map.get($ss, 'primary');
    }

    .page-link {
      color: map.get($ss-theme, 'hunter-grey');
      font-weight: bold;
      &:hover {
        color: #FFF;
        background-color: map.get($ss, 'primary');
      }
    }
  }
}
</style>
